import "./styles.less";

import styled from "@emotion/styled";
import type { NFT } from "@stepfinance/nft";
import type { StepColumnsType } from "@stepfinance/react";
import {
  buildImageURL,
  CheckboxFilter,
  makeSimpleFilters,
  StepButtonDropdown,
  StepTable,
  URLIcon,
} from "@stepfinance/react";
import type { TablePaginationConfig } from "antd/lib/table";
import React, { useEffect, useRef, useState } from "react";

import { ReactComponent as SendIcon } from "../../../assets/mail.svg";
import { ReactComponent as NFTIcon } from "../../../assets/nfts.svg";
import { useAccountsContextRefresher } from "../../../contexts/accounts";
import { useAppConfig } from "../../../contexts/app/config";
import { useEnrichedWallet } from "../../../contexts/enrichedWallet";
import { useLanguageContext } from "../../../contexts/language/language";
import { useNFTContext } from "../../../contexts/nft";
import { useCalculatePriceForDisplayCurrency } from "../../../hooks";
import type { CollapseControlProps } from "../../../hooks/home/useCollapseControl";
import type { CompressedNFT, NFTWithTokenAccount } from "../../../models/nfts/nfts";
import { isCompressedNFT, isNFTWithTokenAccount } from "../../../models/nfts/nfts";
import { fetchAndLoadNFTOffChainData } from "../../../utils/nft/load";
import { formatSolNFTPrice } from "../../../utils/nft/number-utils";
import { isPaginationHidden, slicePages, useLocalStorageState } from "../../../utils/utils";
import { BurnModal } from "../../BurnModal/BurnModal";
import { Modal } from "../../Modal/Modal";
import { NFTLightbox } from "../../nft/NFTLightbox";
import { SendNFTContent } from "../../Send/SendNFTContent";
import { MonospacedText, PAGE_SIZE_OPTIONS, TokenIconText } from "../../shared";
import CellValue from "../../Table/CellValue";
import { ChevronCollapse } from "../ChevronCollapse/ChevronCollapse";
import { NFTNameLink } from "./NFTNameComponent";

export const NFTGrid: React.FC<CollapseControlProps> = ({ activePanels, getPanelKey, onPanelClick }) => {
  const { connected, watching } = useEnrichedWallet();
  // Unlike the NFT Gallery, in the NFTGrid we only display non-staked NFT's.
  // This is because we have a separate section for staked NFT's.
  const { userNFTs, setUserNFTs, NFTBalance } = useNFTContext();

  const { hideableValueWrapper } = useAppConfig();
  const calculatePriceForDisplayCurrency = useCalculatePriceForDisplayCurrency();

  const [viewingNFT, setViewingNFT] = useState<NFT | undefined>(undefined);
  const focusedNFTRef = useRef<HTMLButtonElement>(null);

  const [nftToBeSent, setNftToBeSent] = useState<NFTWithTokenAccount | CompressedNFT | undefined>(undefined);
  const [nftToBurn, setNFTToBurn] = useState<NFTWithTokenAccount | CompressedNFT | null>(null);
  const [paginationSetting, setPaginationSetting] = useLocalStorageState(
    "stepNFTGridPaginationSizeSetting",
    PAGE_SIZE_OPTIONS.l[0],
  );
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    // E.g. if user has 99 NFT's and switches to 100 page size, we still want to allow them to switch
    // back to 20 page size. If user has less than 20, we don't show paging until they have 20+ NFT's.
    current: 1,
    hideOnSinglePage: isPaginationHidden(userNFTs.size, PAGE_SIZE_OPTIONS.l),
    showSizeChanger: true,
    pageSizeOptions: PAGE_SIZE_OPTIONS.l,
    pageSize: parseInt(paginationSetting),
  });
  const [current, setCurrent] = useState(1);

  const refreshTokenAccounts = useAccountsContextRefresher();

  const {
    currentLanguageData: {
      common,
      components: {
        table: { headers },
      },
      pages: {
        nftGallery,
        dashboard: {
          modules: { nftModule },
        },
      },
    },
  } = useLanguageContext();

  useEffect(() => {
    setPagination({
      // E.g. if user has 99 NFT's and switches to 100 page size, we still want to allow them to switch
      // back to 20 page size. If user has less than 20, we don't show paging until they have 20+ NFT's.
      current: current,
      hideOnSinglePage: isPaginationHidden(userNFTs.size, PAGE_SIZE_OPTIONS.l),
      showSizeChanger: true,
      pageSizeOptions: PAGE_SIZE_OPTIONS.l,
      pageSize: parseInt(paginationSetting),
    });
  }, [userNFTs.size, paginationSetting, current]);

  // fetch offchain data for displayed NFTs on initial mount.
  useEffect(() => {
    const nftsToFetch = slicePages(
      [...userNFTs.values()].sort((a, b) => (b.floorPrice ?? 0) - (a.floorPrice ?? 0)),
      pagination.pageSize!,
      pagination.current,
      2,
    );
    fetchAndLoadNFTOffChainData(nftsToFetch, setUserNFTs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTableChange = (pagination, filters, sorter, { currentDataSource }) => {
    const nftsToFetch = slicePages(
      currentDataSource as NFTWithTokenAccount[],
      pagination.pageSize,
      pagination.current,
      2,
    );
    fetchAndLoadNFTOffChainData(nftsToFetch, setUserNFTs);
    setCurrent(pagination.current ? pagination.current : current);
    setPaginationSetting(pagination.pageSize ? pagination.pageSize.toString() : PAGE_SIZE_OPTIONS.l[0]);
  };

  const columns: StepColumnsType<NFT> = [
    {
      title: common.name,
      dataIndex: "name",
      key: "name",
      className: "column-string",
      render(_, record) {
        const imageUrl = record.json?.image;
        const resizedImageUrl = buildImageURL(imageUrl ?? "", "32");

        return (
          <NameContainer>
            <CollectionIconContainer>
              <URLIcon src={resizedImageUrl} />
              <NFTNameLink
                type="link"
                refToUse={focusedNFTRef}
                canUseRef={viewingNFT?.address.toBase58() === record.address.toBase58()}
                onClick={(_e) => {
                  setViewingNFT(record);
                }}
                style={{ padding: 0, border: 0 }}
              >
                <TokenIconText bold>{record.name}</TokenIconText>
              </NFTNameLink>
            </CollectionIconContainer>
            {!watching && (isCompressedNFT(record) || isNFTWithTokenAccount(record)) && (
              <StepButtonDropdown
                buttonTitle={nftModule.sendButton}
                onButtonClick={() => setNftToBeSent(record)}
                items={[
                  {
                    label: nftModule.sendButton,
                    hidden: !(connected && !watching),
                    onClick: () => setNftToBeSent(record),
                  },
                  {
                    label: "Burn",
                    hidden: !(connected && !watching),
                    onClick: () => setNFTToBurn(record),
                  },
                ]}
                data-image={record.json?.image}
              />
            )}
          </NameContainer>
        );
      },
      sorter: (a, b): number => {
        return a.name.localeCompare(b.name);
      },
    },
    {
      title: nftModule.nftCollection,
      dataIndex: "collection",
      key: "collection",
      className: "column-string",
      filters: makeSimpleFilters([...userNFTs].map((v) => v[1].collectionName)),
      filterDropdown: (props) => (
        <CheckboxFilter
          searchable
          searchPlaceholder={nftGallery.searchCollection}
          emptySearchResultsPrompt={nftGallery.noCollectionFound}
          {...props}
        />
      ),
      onFilter: (value, record) => record.collectionName.startsWith(value.toString()),
      render(_, record) {
        return <CellValue cellType="text">{record.collectionName}</CellValue>;
      },
      sorter: (a, b) => {
        const collectionA = a.collectionName;
        const collectionB = b.collectionName;

        return (collectionA || "").localeCompare(collectionB || "");
      },
    },
    {
      title: common.balance,
      dataIndex: "balance",
      key: "balance",
      className: "column-number",
      width: "110px",
      render(_, record) {
        return <CellValue cellType="number">{record.amount}</CellValue>;
      },
      sorter: (a, b) => {
        return a.amount - b.amount;
      },
    },
    {
      title: headers.price,
      dataIndex: "tokenPrice",
      key: "tokenPrice",
      className: "column-number",
      render(_, record) {
        return (
          <CellValue cellType="text">
            <MonospacedText>{hideableValueWrapper(formatSolNFTPrice(record.floorPrice))} SOL </MonospacedText>
            <MonospacedText style={{ color: "#7d7d7d" }}>
              ({calculatePriceForDisplayCurrency(record.floorPriceUSD)})
            </MonospacedText>
          </CellValue>
        );
      },
      sorter: (a, b) => {
        return (a.floorPrice ?? 0) - (b.floorPrice ?? 0);
      },
    },
    {
      title: common.value,
      dataIndex: "balanceInUSD",
      key: "balanceInUSD",
      className: "column-number",
      render(_, { floorPrice, floorPriceUSD, amount }) {
        const valueInSOL = floorPrice !== null ? floorPrice * amount : null;
        const valueInUSD = floorPriceUSD !== null ? floorPriceUSD * amount : null;
        return (
          <CellValue cellType="number">
            <MonospacedText style={{ fontWeight: 700 }}>
              {hideableValueWrapper(formatSolNFTPrice(valueInSOL))} SOL{" "}
            </MonospacedText>
            <MonospacedText style={{ color: "#7d7d7d" }}>
              ({calculatePriceForDisplayCurrency(valueInUSD)})
            </MonospacedText>
          </CellValue>
        );
      },
      sorter: (a, b) => {
        return (a.floorPrice ?? 0) * a.amount - (b.floorPrice ?? 0) * b.amount;
      },
      defaultSortOrder: "descend",
    },
  ];

  return (
    <>
      {userNFTs.size > 0 ? (
        <div style={{ marginTop: 20 }}>
          <ChevronCollapse
            value={NFTBalance}
            title={nftModule.title}
            TitleIcon={NFTIcon}
            activePanels={activePanels}
            panelKey={getPanelKey("NFTGrid")}
            onChange={onPanelClick}
          >
            <>
              <StepTable<NFT>
                columns={columns}
                dataSource={[...userNFTs.values()]}
                pagination={pagination}
                rowKey={(record: NFT) => record.address.toBase58()}
                onChange={onTableChange}
              />
              {viewingNFT && <NFTLightbox focusedNFTRef={focusedNFTRef} viewingNFT={viewingNFT} />}
            </>
          </ChevronCollapse>
          <Modal
            TitleIcon={SendIcon}
            title={nftModule.sendButton}
            width={500}
            onClose={() => setNftToBeSent(undefined)}
            visible={!!nftToBeSent}
          >
            {nftToBeSent && <SendNFTContent nft={nftToBeSent} closeModal={() => setNftToBeSent(undefined)} />}
          </Modal>
          <BurnModal
            data={nftToBurn}
            onSuccess={() => {
              setUserNFTs((currentState) => {
                const nftMap = new Map(currentState);
                nftMap.delete(nftToBurn?.address.toBase58() ?? "");
                return nftMap;
              });
              refreshTokenAccounts();
            }}
            onClose={() => setNFTToBurn(null)}
            visible={!!nftToBurn}
            width={500}
          />
        </div>
      ) : null}
    </>
  );
};

const NameContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CollectionIconContainer = styled.div`
  display: flex;
  align-items: center;
`;
